import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Modal,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import {
  Search,
  QrCode,
  Envelope,
  Telephone,
  Droplet,
  Eye,
  FileText,
  InfoCircle,
  CheckCircle,
  ChevronRight,
  Prescription
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
// Données fictives des patients (identiques à celles du tableau de bord médecin)
const mockPatients = [
  {
    id: 'CNAMGS-123456',
    name: 'Jean Dupont',
    birthDate: '15/05/1985',
    lastVisit: '2025-03-12',
    history: 'Hypertension, allergie aux pénicillines',
    phone: '+241 01 23 45 67',
    email: 'jean.dupont@email.ga',
    bloodType: 'A+',
    photo: 'https://randomuser.me/api/portraits/men/1.jpg',
  },
  {
    id: 'CNAMGS-789012',
    name: 'Marie Nzogho',
    birthDate: '23/09/1990',
    lastVisit: '2025-03-10',
    history: 'Asthme, diabète type 2',
    phone: '+241 01 23 45 68',
    email: 'marie.nzogho@email.ga',
    bloodType: 'O+',
    photo: 'https://randomuser.me/api/portraits/women/2.jpg',
  },
  {
    id: 'CNAMGS-345678',
    name: 'Pierre Mba',
    birthDate: '10/12/1978',
    lastVisit: '2025-03-05',
    history: 'Aucun antécédent notable',
    phone: '+241 01 23 45 69',
    email: 'pierre.mba@email.ga',
    bloodType: 'B+',
    photo: 'https://randomuser.me/api/portraits/men/3.jpg',
  },
];

const PatientSearch = () => {
    const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4000);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      showToast('Veuillez saisir un nom ou un numéro d’assuré', 'warning');
      return;
    }

    setSearching(true);
    setTimeout(() => {
      const term = searchTerm.toLowerCase();
      const filtered = mockPatients.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.id.toLowerCase().includes(term) ||
          p.phone.includes(term)
      );
      setResults(filtered);
      setSearching(false);
      if (filtered.length === 0) {
        showToast('Aucun patient trouvé', 'danger');
      } else {
        showToast(`${filtered.length} patient(s) trouvé(s)`, 'success');
      }
    }, 500);
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer
        className="p-3"
        style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}
      >
        <Toast
          show={toast.show}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          bg={toast.variant}
          delay={4000}
          autohide
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success'
                ? 'Succès'
                : toast.variant === 'danger'
                ? 'Erreur'
                : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div
          className="welcome-section mb-4 p-4 rounded-4"
          style={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
          }}
        >
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-circle p-3">
                  <QrCode size={48} className="text-primary" />
                </div>
                <div>
                  <h1 className="h3 fw-bold mb-1">Rechercher un patient</h1>
                  <p className="mb-0 opacity-90">
                    Utilisez le nom, le numéro d’assuré ou le téléphone pour retrouver rapidement un
                    patient.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Barre de recherche */}
        <Card className="border-0 shadow-sm mb-4 bg-white" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <InputGroup>
              <InputGroup.Text className="bg-light border-0">
                <Search size={18} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Nom, numéro d’assuré ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-0 bg-light"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button
                variant="success"
                onClick={handleSearch}
                disabled={searching}
                className="rounded-pill px-4"
              >
                {searching ? <Spinner size="sm" className="me-1" /> : <Search size={16} className="me-1" />}
                {searching ? 'Recherche...' : 'Rechercher'}
              </Button>
              {searchTerm && (
                <Button variant="outline-secondary" onClick={handleClear} className="rounded-pill">
                  Effacer
                </Button>
              )}
            </InputGroup>
          </Card.Body>
        </Card>

        {/* Résultats - pleine largeur */}
        {results.length > 0 && (
          <Row className="g-4">
            {results.map((patient) => {
              // Calcul de l'âge
              const birthDate = new Date(patient.birthDate);
              let age = new Date().getFullYear() - birthDate.getFullYear();
              const monthDiff = new Date().getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && new Date().getDate() < birthDate.getDate())) {
                age--;
              }

              // Données supplémentaires fictives
              const nextAppointment =
                patient.id === 'CNAMGS-123456'
                  ? '05/04/2025'
                  : patient.id === 'CNAMGS-789012'
                  ? '10/04/2025'
                  : '15/04/2025';
              const prescriptionsCount =
                patient.id === 'CNAMGS-123456' ? 5 : patient.id === 'CNAMGS-789012' ? 3 : 2;

              return (
                <Col xs={12} key={patient.id}>
                  <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                    {/* En-tête avec gradient */}
                    <div
                      className="p-3"
                      style={{
                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <FileText size={18} className="text-white" />
                          <h6 className="mb-0 fw-semibold text-white">Dossier patient</h6>
                        </div>
                        <Badge bg="light" text="dark" className="rounded-pill px-2 py-1">
                          {patient.id}
                        </Badge>
                      </div>
                    </div>

                    <Card.Body className="p-4">
                      <Row className="align-items-center g-4">
                        {/* Photo et statut */}
                        <Col xs={12} md={2} className="text-center">
                          <div className="position-relative d-inline-block">
                            <div
                              style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: `3px solid #fff`,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                margin: '0 auto',
                                background: '#f0f0f0',
                              }}
                            >
                              <img
                                src={patient.photo}
                                alt={patient.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                            <div
                              className="position-absolute bottom-0 end-0 bg-success rounded-circle p-1"
                              style={{ transform: 'translate(10%, 10%)' }}
                            >
                              <CheckCircle size={12} className="text-white" />
                            </div>
                          </div>
                          <div className="mt-2">
                            <Badge bg="success" className="rounded-pill px-2 py-1 small">
                              Actif
                            </Badge>
                          </div>
                        </Col>

                        {/* Informations principales */}
                        <Col xs={12} md={4}>
                          <div className="mb-2">
                            <small className="text-muted d-block">Nom complet</small>
                            <strong className="fs-5">{patient.name}</strong>
                          </div>
                      
                          <div className="mb-2">
                            <small className="text-muted d-block">Dernière visite</small>
                            <strong>{formatDate(patient.lastVisit)}</strong>
                          </div>
                        </Col>

                        {/* Coordonnées */}
                        <Col xs={12} md={3}>
                          <div className="mb-2 d-flex align-items-center">
                            <Telephone size={14} className="text-muted me-2" />
                            <span className="small">{patient.phone}</span>
                          </div>
                          <div className="mb-2 d-flex align-items-center">
                            <Envelope size={14} className="text-muted me-2" />
                            <span className="small">{patient.email}</span>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted d-block">Ordonnances</small>
                            <strong>{prescriptionsCount}</strong>
                          </div>
                        </Col>

                        {/* Actions et prochain rendez-vous */}
                        <Col xs={12} md={3}>
                          <div className="bg-light rounded-3 p-2 mb-3 text-center">
                            <small className="text-muted d-block">Prochain rendez-vous</small>
                            <strong>{nextAppointment}</strong>
                            <Button variant="link" size="sm" className="p-0 mt-1 text-decoration-none">
                              <ChevronRight size={14} />
                            </Button>
                          </div>
                          <div className="d-flex gap-2">
  <Button
    variant="outline-primary"
    size="sm"
    className="flex-grow-1 rounded-pill d-flex align-items-center justify-content-center"
     onClick={() => navigate(`/doctor/patient-record/${patient.id}`)}
  >
    <Eye size={14} className="me-1" /> Détails
  </Button>
  <Button
    variant="success"
    size="sm"
    className="flex-grow-1 rounded-pill d-flex align-items-center justify-content-center"
    onClick={() => navigate(`/doctor/prescribe/${patient.id}`)}
  >
    <Prescription size={14} className="me-1" /> Prescrire
  </Button>
</div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Aucun résultat */}
        {!searching && results.length === 0 && searchTerm && (
          <Card className="border-0 shadow-sm text-center py-5" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <Search size={48} className="text-muted mb-3" />
              <h5>Aucun patient trouvé</h5>
              <p className="text-muted mb-0">
                Aucun patient ne correspond à « {searchTerm} ». Vérifiez l’orthographe ou essayez un
                autre critère.
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Modal de détails (inchangé) */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <FileText size={20} />
              Dossier médical complet
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPatient && (
              <>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Nom complet</small>
                      <strong>{selectedPatient.name}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Numéro d'assuré</small>
                      <strong>{selectedPatient.id}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Date de naissance</small>
                      <strong>{selectedPatient.birthDate}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Dernière visite</small>
                      <strong>{formatDate(selectedPatient.lastVisit)}</strong>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="p-3 rounded-3" style={{ background: '#f8f9fa' }}>
                      <small className="text-muted d-block">Antécédents médicaux</small>
                      <strong>{selectedPatient.history}</strong>
                    </div>
                  </Col>
                </Row>
                <hr />
                <div className="mt-3">
                  <h6 className="fw-semibold mb-3">Contact</h6>
                  <div className="mb-2">
                    <Telephone size={14} className="me-2" /> {selectedPatient.phone}
                  </div>
                  <div className="mb-2">
                    <Envelope size={14} className="me-2" /> {selectedPatient.email}
                  </div>
                  <div>
                    <Droplet size={14} className="me-2 text-danger" /> Groupe sanguin : {selectedPatient.bloodType}
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15) !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PatientSearch;